import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill, formatUGX } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "180px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  // Toolbar & Search
  toolbarWrap: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    marginBottom: "24px",
    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.03)",
    border: "1px solid #f1f5f9"
  },
  searchBar: {
    display: "flex",
    gap: "12px",
    marginTop: "16px",
    alignItems: "center",
    flexWrap: "wrap"
  },
  searchInput: {
    flex: 1,
    minWidth: "280px",
    "& .MuiInputBase-root": {
      borderRadius: "10px",
      background: "#f8fafc",
      padding: "4px 12px",
      border: "1px solid #e2e8f0",
      transition: "all 0.2s ease",
      "&:hover": { borderColor: "#cbd5e1" },
      "&.Mui-focused": { background: "#fff", borderColor: "#3f51b5", boxShadow: "0 0 0 4px rgba(63, 81, 181, 0.1)" }
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" }
  },
  filterSelect: {
    height: "45px",
    minWidth: "160px",
    borderRadius: "10px",
    padding: "0 12px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "14px",
    color: "#475569",
    outline: "none",
    cursor: "pointer",
    "&:focus": { borderColor: "#3f51b5", background: "#fff" }
  },

  tableCard: {
    borderRadius: "16px",
    boxShadow: "0 4px 24px 0 rgba(0,0,0,0.04)",
    border: "1px solid #f1f5f9"
  },
  tableHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  tableTitle: { fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 },
  tableSubtitle: { fontSize: "13px", color: "#64748b", margin: "2px 0 0" },

  errorMsg: { color: "#f44336", fontSize: "13px" },
  link: {
    color: "#3f51b5",
    cursor: "pointer",
    fontWeight: "600",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" }
  },
  actionBtn: { borderRadius: "8px", textTransform: "none", fontWeight: 600 }
};

class ArrearsReport extends Component {
  state = {
    arrears: [],
    semesters: [],
    programmes: [],
    selectedSemester: "",
    selectedProgramme: "",
    search: "",
    loading: false,
    error: null,
    totals: { students: 0, invoiced: 0, paid: 0, outstanding: 0 }
  };

  componentDidMount() { this.fetchMeta(); }

  fetchMeta = async () => {
    try {
      const [sRes, pRes] = await Promise.all([
        api.get("/semesters?limit=100"),
        api.get("/programmes?limit=100").catch(() => ({ data: { data: [] } }))
      ]);
      const sd = sRes.data.data || sRes.data;
      const pd = pRes.data.data || pRes.data;
      const semesters = Array.isArray(sd) ? sd : sd.data || [];
      const current = semesters.find(s => s.isCurrent);
      this.setState({
        semesters,
        programmes: Array.isArray(pd) ? pd : pd.data || [],
        selectedSemester: current ? current.id : ""
      }, () => { if (this.state.selectedSemester) this.fetchArrears(); });
    } catch (e) { /* silent */ }
  };

  fetchArrears = async () => {
    const { selectedSemester, selectedProgramme, search } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const params = { limit: 500 };
      if (selectedSemester) params.semesterId = selectedSemester;
      if (selectedProgramme) params.programmeId = selectedProgramme;
      if (search) params.search = search;
      const res = await api.get("/fees/arrears", { params });
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      const totals = list.reduce((acc, r) => ({
        students: acc.students + 1,
        invoiced: acc.invoiced + (r.totalInvoiced || 0),
        paid: acc.paid + (r.totalPaid || 0),
        outstanding: acc.outstanding + (r.balance || 0)
      }), { students: 0, invoiced: 0, paid: 0, outstanding: 0 });
      this.setState({ arrears: list, totals, loading: false });
    } catch (e) {
      this.setState({ error: "Failed to load arrears.", loading: false });
    }
  };

  handleSearch = e => this.setState({ search: e.target.value });
  handleSearchSubmit = e => { e.preventDefault(); this.fetchArrears(); };

  exportCSV = () => {
    const { arrears, totals } = this.state;
    if (!arrears.length) return;

    const headers = ["Student No", "Student Name", "Programme", "Invoiced", "Paid", "Balance", "% Paid"];
    const rows = arrears.map(r => [
      r.student?.studentNo || "",
      `${r.student?.firstName || ""} ${r.student?.lastName || ""}`.trim(),
      r.student?.programme?.name || "",
      r.totalInvoiced || 0,
      r.totalPaid || 0,
      r.balance || 0,
      r.totalInvoiced > 0 ? `${Math.round((r.totalPaid / r.totalInvoiced) * 100)}%` : "0%"
    ]);

    // Add totals row
    rows.push([]);
    rows.push(["TOTALS", `${totals.students} Students`, "", totals.invoiced, totals.paid, totals.outstanding, ""]);

    const csvContent = [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `arrears_report_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    const { classes, history } = this.props;
    const { arrears, semesters, programmes, selectedSemester, selectedProgramme, search, loading, error, totals } = this.state;

    const columns = [
      {
        id: "studentNo",
        header: "Std No",
        accessorFn: r => r.student?.studentNo || "—",
        meta: { variant: "mono" },
        cell: info => (
          <span className={classes.link} onClick={() => history.push(`/bwillis/students/${info.row.original.student?.id}`)}>
            {info.getValue()}
          </span>
        )
      },
      {
        id: "student",
        header: "Student",
        accessorFn: r => `${r.student?.firstName || ""} ${r.student?.lastName || ""}`.trim() || "—",
        cell: info => <div style={{ fontWeight: 600, color: "#1e293b" }}>{info.getValue()}</div>
      },
      {
        id: "programme",
        header: "Programme",
        accessorFn: r => r.student?.programme?.name || "—",
        cell: info => <span style={{ color: "#475569" }}>{info.getValue()}</span>
      },
      {
        id: "invoiced",
        header: "Invoiced (UGX)",
        accessorFn: r => r.totalInvoiced || 0,
        meta: { align: "right" },
        cell: info => formatUGX(info.getValue())
      },
      {
        id: "paid",
        header: "Paid (UGX)",
        accessorFn: r => r.totalPaid || 0,
        meta: { align: "right" },
        cell: info => formatUGX(info.getValue())
      },
      {
        id: "balance",
        header: "Balance (UGX)",
        accessorFn: r => r.balance || 0,
        meta: { align: "right" },
        cell: info => {
          const balance = info.getValue();
          return <Pill tone={balance > 0 ? "danger" : "success"}>{formatUGX(balance)}</Pill>;
        }
      },
      {
        id: "pctPaid",
        header: "% Paid",
        accessorFn: r => r.totalInvoiced > 0 ? Math.round((r.totalPaid / r.totalInvoiced) * 100) : null,
        cell: info => {
          const v = info.getValue();
          return <span style={{ color: "#64748b", fontSize: "12px", fontWeight: 500 }}>{v != null ? `${v}%` : "—"}</span>;
        }
      }
    ];

    const statCards = [
      { label: "Students with Arrears", value: totals.students, icon: "people", bg: "#e8eaf6", text: "#1a237e" },
      { label: "Total Invoiced", value: totals.invoiced.toLocaleString(), icon: "description", bg: "#f5f5f5", text: "#333" },
      { label: "Total Paid", value: totals.paid.toLocaleString(), icon: "payments", bg: "#e8f5e9", text: "#2e7d32" },
      { label: "Total Outstanding", value: totals.outstanding.toLocaleString(), icon: "account_balance_wallet", bg: "#ffebee", text: "#b71c1c" }
    ];

    return (
      <div>
        {/* ── Hero Section ─────────────────────────────────────────────── */}
        <div className={classes.heroWrap}>
          <div className={classes.heroOverlay} />
          <div className={classes.heroContent}>
            <h1 className={classes.heroTitle}>Fee Arrears Report</h1>
            <p className={classes.heroSub}>Track and manage outstanding student balances across programmes</p>
            <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
              <Button color="white" className={classes.actionBtn} onClick={this.exportCSV} disabled={!arrears.length}>
                <Icon style={{ marginRight: "8px" }}>file_download</Icon> Export CSV
              </Button>
              <Button color="white" simple className={classes.actionBtn} onClick={() => window.print()}>
                <Icon style={{ marginRight: "8px" }}>print</Icon> Print Report
              </Button>
            </div>
          </div>
        </div>

        {/* ── KPI Stats ────────────────────────────────────────────────── */}
        <div className={classes.kpiRow}>
          {statCards.map(s => (
            <div key={s.label} className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: s.bg }}>
                <Icon style={{ color: s.text }}>{s.icon}</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{s.label.includes("Total") ? `UGX ${s.value}` : s.value}</h3>
                <p className={classes.statLabel}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters & Search ─────────────────────────────────────────── */}
        <div className={classes.toolbarWrap}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <h4 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>Filters</h4>
          </div>
          <form onSubmit={this.handleSearchSubmit} className={classes.searchBar}>
            <CustomInput
              className={classes.searchInput}
              formControlProps={{ style: { margin: 0, paddingTop: 0 } }}
              inputProps={{
                placeholder: "Search by student name or number...",
                value: search,
                onChange: this.handleSearch,
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon style={{ color: "#94a3b8", cursor: "pointer" }} onClick={this.fetchArrears}>search</Icon>
                  </InputAdornment>
                )
              }}
            />
            <FormControl className={classes.formControl} style={{ minWidth: "200px", marginTop: 0 }}>
              <Select
                value={selectedSemester}
                onChange={e => this.setState({ selectedSemester: e.target.value }, this.fetchArrears)}
                className={classes.filterSelect}
                displayEmpty
              >
                <MenuItem value="">All Semesters</MenuItem>
                {semesters.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}{s.isCurrent ? " (Current)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl} style={{ minWidth: "200px", marginTop: 0 }}>
              <Select
                value={selectedProgramme}
                onChange={e => this.setState({ selectedProgramme: e.target.value }, this.fetchArrears)}
                className={classes.filterSelect}
                displayEmpty
              >
                <MenuItem value="">All Programmes</MenuItem>
                {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Button color="primary" type="submit" className={classes.actionBtn}>
              Apply Filters
            </Button>
          </form>
        </div>

        <GridContainer>
          <GridItem xs={12}>
            {error && <p className={classes.errorMsg} style={{ padding: "0 0 12px" }}>{error}</p>}

            <DataTable
              title="Arrears List"
              subtitle={`Showing ${arrears.length} students with outstanding balances`}
              columns={columns}
              data={arrears}
              isLoading={loading}
              emptyState={
                <div>
                  <Icon style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}>account_balance</Icon>
                  <p>{selectedSemester ? "No arrears found for the selected filters." : "Select a semester to view arrears."}</p>
                </div>
              }
            />

            {arrears.length > 0 && (
              <CardFooter style={{ padding: "16px 24px", borderTop: "1px solid #f1f5f9" }}>
                <span style={{ fontSize: "14px", color: "#64748b", fontWeight: 500 }}>
                  Total outstanding across listed students: <strong style={{ color: "#b71c1c", marginLeft: "8px" }}>UGX {totals.outstanding.toLocaleString()}</strong>
                </span>
              </CardFooter>
            )}
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

ArrearsReport.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(ArrearsReport));
